import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { onSnapshot, query, where, type Unsubscribe } from 'firebase/firestore'
import type { ContactDoc, ContactEntity } from '@common/models/contact/contact.modl'
import { toContactEntity } from '@common/models/contact/contact.modl'
import { refs } from '@common/models/base/firestore-refs'
import { useSchoolAccount } from './SchoolAccountContext'

type ContactContextType = {
  contacts: ContactEntity[];
  isLoading: boolean;
  error: Error | null;
};

const ContactContext = createContext<ContactContextType>({
  contacts: [],
  isLoading: true,
  error: null,
})

export const ContactProvider = ({ children }: { children: ReactNode }) => {
  const { schoolAccount, isLoading: accountLoading } = useSchoolAccount();
  const [contacts, setContacts] = useState<ContactEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (accountLoading || !schoolAccount) {
      setIsLoading(accountLoading);
      if (!accountLoading && !schoolAccount) {
        setContacts([]);
        setError(null);
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const schoolAccountId = schoolAccount.id
      const contactCollectionRef = refs.contacts(schoolAccountId)

      // is_deleted == false かつ status != 'completed' の条件でフィルタリング
      const q = query(
        contactCollectionRef,
        where('is_deleted', '==', false),
        where('status', '!=', 'completed')
      );

      // リアルタイム監視
      const unsubscribe: Unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetchedContacts: ContactEntity[] = [];

          snapshot.docs.forEach((docSnap) => {
            try {
              const contact = toContactEntity(docSnap.id, docSnap.data() as ContactDoc, docSnap.ref.path)
              if (contact) {
                contact.school_account_id = schoolAccountId
                fetchedContacts.push(contact);
              }
            } catch (err) {
              console.error('Contact parse error:', err);
            }
          });

          fetchedContacts.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
          setContacts(fetchedContacts);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Failed to subscribe contacts:', err);
          setError(err instanceof Error ? err : new Error('お問い合わせの取得に失敗しました'));
          setIsLoading(false);
        }
      );

      // クリーンアップ関数
      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error('Contact subscription setup error:', err);
      setError(err instanceof Error ? err : new Error('お問い合わせの監視設定に失敗しました'));
      setIsLoading(false);
      return undefined;
    }
  }, [schoolAccount, accountLoading]);

  return (
    <ContactContext.Provider value={{ contacts, isLoading, error }}>
      {children}
    </ContactContext.Provider>
  );
};

export const useContact = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error('useContact must be used within a ContactProvider');
  }
  return context;
};
